import React, { useEffect } from 'react';
import {Doughnut} from 'react-chartjs-2';
import ApiService from '../../services/apiService';
import { useOktaAuth } from '@okta/okta-react';
import { DEVELOPER_PROGRAM_COLORS, DEVELOPER_PROGRAM_COLORS_DARK } from './shared/chartColors';
import { CHART_DEFAULTS, CHART_TITLES } from './shared/chartConstants';
import { createBaseChartOptions, createContainerStyles } from './shared/chartConfigFactory';
import { useNavigate } from 'react-router-dom';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { useDarkTheme } from '../../contexts/DarkThemeContext';


// Constants for Developer Program chart labels
const CHART_LABELS = {
    NON_DEVELOPER_PROGRAM: 'APIs Not in Developer Program',
    DEVELOPER_PROGRAM: 'Developer Program APIs'
};

const ApiDeveloperProgram: React.FC = () => {
    const { authState } = useOktaAuth();
    const navigate = useNavigate();
    const { isDarkTheme } = useDarkTheme();
    const [apiData, setApiData] = React.useState<any[]>([]);
    const [chartData, setChartData] = React.useState<number[]>([]);
    const [chartLabels, setChartLabels] = React.useState<string[]>([]);
    useWindowDimensions();

    const handleChartClick = (event: any, elements: any[]) => {
        if (elements.length > 0) {
            const clickedIndex = elements[0].index;
            const clickedLabel = chartLabels[clickedIndex];
            console.log('Chart clicked:', clickedLabel);
            
            // Create URL parameters based on the clicked segment
            const params = new URLSearchParams();
            
            if (clickedLabel === 'Developer Program APIs') {
                params.set('developerProgram', 'Yes');
            } else if (clickedLabel === 'APIs Not in Developer Program') {
                params.set('developerProgram', 'No');
            }
            
            // Navigate to table view with filters
            navigate(`/table-view?${params.toString()}`);
        }
    };

    const extractData = (apiData: any[]) => {
        
        // Count total APIs and Developer Program APIs
        const totalApis = apiData.length;
        const developerProgramApis = apiData.filter(api => api.developerProgram === true).length;
        const standardApis = totalApis - developerProgramApis;
        
        return { totalApis, developerProgramApis, standardApis };
    }

    const processMapData = (apiCounts: { totalApis: number, developerProgramApis: number, standardApis: number }) => {
        
        // Create a visualization showing the subset relationship
        // Show: APIs not in Developer Program, and Developer Program APIs
        const nonDeveloperApis = apiCounts.totalApis - apiCounts.developerProgramApis;
        
        const labels = [CHART_LABELS.NON_DEVELOPER_PROGRAM, CHART_LABELS.DEVELOPER_PROGRAM];
        const values = [nonDeveloperApis, apiCounts.developerProgramApis];
        
        setChartLabels(labels);
        setChartData(values);
    }

    const getData = async() => {
        try {
            const apiService = new ApiService();
            const data = (await apiService.getAllApis(authState)) as any[];            
            setApiData(data);
            
            const apiCounts = extractData(data);
            processMapData(apiCounts);
        } catch (error) {
            console.error("Error fetching API data for the Graphics:", error);
        } 
    }

    useEffect(() => {
        getData();
    }, [authState]);

    // Use shared chart configuration
    const options = createBaseChartOptions({
        title: CHART_TITLES.DEVELOPER_PROGRAM_PARTICIPATION,
        chartLabels: chartLabels,
        onClick: handleChartClick,
        isDark: isDarkTheme
    });

    const labels = chartLabels;
    
    const devColors = isDarkTheme ? DEVELOPER_PROGRAM_COLORS_DARK : DEVELOPER_PROGRAM_COLORS;

    const data = {
        labels,
        datasets: [
            {
                data: chartData,
                backgroundColor: [
                    devColors.BACKGROUND.NON_DEVELOPER_PROGRAM,
                    devColors.BACKGROUND.DEVELOPER_PROGRAM,
                ],
                borderColor: [
                    devColors.BORDER.NON_DEVELOPER_PROGRAM,
                    devColors.BORDER.DEVELOPER_PROGRAM,
                ],
                borderWidth: CHART_DEFAULTS.BORDER_WIDTH,
            },
        ],
    }

    return (
        <div className="graphic-content chart-with-title-spacing" style={createContainerStyles()}>
            <Doughnut options={options} data={data} />
        </div>
    )
}

export default ApiDeveloperProgram;